import { designVariables } from "@studio3t/design-variables";
import {
  type ComponentPropsWithoutRef,
  useEffect,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { trapTabKey } from "./focusTrap";

const MODAL = designVariables.ui.modal;
const H4 = designVariables.type.heading.h4;

// The app-wide modal chrome (Figma Modal demo 14669-155335), extracted from
// the NewStudio NameModal so every dialog shares one look: token backdrop,
// 10px-corner card with the elevation/outset5 shadow, 30/30 header with the
// H4 title (no divider), 0/30/26 body, and a footer band — hairline on top,
// 20/30 padding, right-aligned actions at the 10px footer gap. Modals own
// their behavior (focus, Escape, submit); this module owns only the look.
// RawConfigModal intentionally keeps its own chrome.

// Whether ANY modal is up, counted by mounted backdrops. Some interactions
// listen at the document level and hit-test by coordinates (the studio's
// panel resize handles), so the backdrop element alone cannot block them —
// those consumers subscribe here and disable themselves instead.
/** The backdrop's stacking level. Floating UI a modal opens (a dropdown
 *  menu portalled to the body) must sit above it — pass
 *  `MODAL_Z_INDEX + 1` as the library's floatingZIndex, or the menu opens
 *  underneath the backdrop, invisible. */
export const MODAL_Z_INDEX = 1000;

let openModals = 0;
const modalListeners = new Set<() => void>();
const subscribeModalOpen = (listener: () => void) => {
  modalListeners.add(listener);
  return () => {
    modalListeners.delete(listener);
  };
};
export function useAnyModalOpen(): boolean {
  return useSyncExternalStore(subscribeModalOpen, () => openModals > 0);
}
/** The same answer read outside React — for a check at the moment an
 *  action runs (a deferred callback), where a rendered value may be
 *  stale. */
export function isAnyModalOpen(): boolean {
  return openModals > 0;
}

/** Counts this modal among the open ones while `open` holds — what
 *  ModalBackdrop does for every modal on the shared chrome, exposed for
 *  the one that keeps its own backdrop (RawConfigModal) so the app-wide
 *  gates (⌘K, the studio hotkeys, the panel resize handles) see it too. */
export function useModalPresence(open: boolean) {
  useEffect(() => {
    if (!open) return;
    openModals += 1;
    for (const listener of modalListeners) listener();
    return () => {
      openModals -= 1;
      for (const listener of modalListeners) listener();
    };
  }, [open]);
}

const ModalBackdropBox = styled.div`
  position: fixed;
  inset: 0;
  background: ${(p) => p.theme.modalBackdropBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${MODAL_Z_INDEX};
`;

/** The full-screen backdrop every modal renders; mounting one flips
 *  useAnyModalOpen for the app. Portalled to the body: rendered in place,
 *  a page's OverlayScrollbars viewports (z-index: 0) trap the fixed
 *  backdrop in their stacking context, and anything outside them — the
 *  pages' sticky headers, the studio's workbench panels — paints over the
 *  modal (the burial SchemaDelete and TestPreview used to dodge with
 *  their own FloatingPortal). ListPageHeader pins itself expecting
 *  overlays to be portalled. */
export function ModalBackdrop(props: ComponentPropsWithoutRef<"div">) {
  useModalPresence(true);
  // Tab stays inside the card (focusTrap): the backdrop is the closest
  // ancestor every modal shares, and keydown bubbles up to it from any
  // focused control in the card.
  return createPortal(
    <ModalBackdropBox onKeyDown={trapTabKey} {...props} />,
    document.body,
  );
}

/** The card. Width varies per modal — set it via styled(ModalCard). */
export const ModalCard = styled.div`
  max-width: calc(100vw - 40px);
  background: ${(p) => p.theme.modalBackground};
  border-radius: ${MODAL.corner}px;
  box-shadow:
    0 34px 64px rgba(17, 17, 17, 0.13),
    0 4px 34px rgba(17, 17, 17, 0.15);
  overflow: hidden;
  outline: 0;
`;

/** Hosts the absolutely-positioned ModalCloseButton ($top/$right 30/30). */
export const ModalHeader = styled.div`
  position: relative;
  padding: ${MODAL.header.paddingh}px ${MODAL.paddingw}px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${H4.fontSize}px;
  font-weight: ${H4.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextHeading};
`;

export const ModalBody = styled.div`
  padding: 0 ${MODAL.paddingw}px ${MODAL.paddingb}px;
`;

/** The footer band. */
export const ModalFooter = styled.div`
  background: ${(p) => p.theme.modalFooterBackground};
  border-top: 1px solid ${(p) => p.theme.semanticBorderLight};
  /* Scroll-body modals lay the card out as a flex column — the footer
     band never gives up its height to the scrolling region. */
  flex-shrink: 0;
`;

/**
 * Right-aligned actions, Cancel left of the accepting action (macOS order).
 * A third action splits the row: pass $split and put Cancel alone on the
 * left, the rest inside a right-side group.
 */
export const ModalFooterActions = styled.div<{ $split?: boolean }>`
  display: flex;
  justify-content: ${(p) => (p.$split ? "space-between" : "flex-end")};
  align-items: center;
  gap: ${MODAL.footer.gap}px;
  padding: ${MODAL.footer.paddingh}px ${MODAL.paddingw}px;
`;

/** A button cluster inside a $split footer (e.g. the right-side pair). */
export const ModalFooterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${MODAL.footer.gap}px;
`;
